<template>
  <div class="mobile-filter">
    <tk-search-input class="mt-3 mb-2" @click:append="$emit('search')" />

    <v-list class="pt-0 list-with-checboxes">
      <!-- Techniques filter -->
      <v-list-tile>
        <v-list-tile-content>
          <v-expansion-panel>
            <v-expansion-panel-content>
              <template v-slot:header>
                <div class="font-weight-medium">{{ $t('ARTWORK.TECHNIQUE') }}</div>
              </template>
              <v-treeview
                :items="techniques"
                :value="filters.techniquesSelected"
                selected-color="primary"
                class="ml-1 no-hover"
                item-value="key"
                open-on-click
                selectable
                transition
                return-object
                @input="onSelectedTechniquesChange"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-list-tile-content>
      </v-list-tile>

      <v-divider />

      <!-- Themes filter -->
      <v-list-group>
        <template v-slot:activator>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ $t('ARTWORK.THEME') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>

        <v-list-tile v-for="theme in themes" :key="theme.id">
          <v-list-tile-action>
            <v-checkbox color="black" v-model="filters.themesSelected" :value="theme" />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ theme.name }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list-group>

      <v-divider />

      <!-- Size filter -->
      <v-list-group>
        <template v-slot:activator>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ $t('ARTWORK.SIZE') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>

        <v-list-tile v-for="size in sizes" :key="size.id">
          <v-list-tile-action>
            <v-checkbox v-model="filters.sizesSelected" :value="size" color="black" />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ size.name }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list-group>

      <v-divider />

      <!-- Orientations filter -->
      <v-list-group>
        <template v-slot:activator>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ $t('ARTWORK.ORIENTATION') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>

        <v-list-tile v-for="orientation in orientations" :key="orientation.id">
          <v-list-tile-action>
            <v-checkbox v-model="filters.orientationsSelected" :value="orientation" color="black" />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ orientation.name }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list-group>

      <v-divider />

      <!-- Price filter-->
      <v-list-group class="mb-0">
        <template v-slot:activator>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ $t('ARTWORK.PRICE') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>

        <v-list-tile class="price-tile">
          <v-list-tile-content class="pa-0 mb-3">
            <price-slider :filters="filters" />
          </v-list-tile-content>
        </v-list-tile>
      </v-list-group>

      <v-divider />

      <!-- Colors filter -->
      <v-list-group>
        <template v-slot:activator>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title class="font-weight-medium">{{ $t('ARTWORK.COLOR') }}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>

        <v-list-tile
          tag="span"
          v-for="color in colors"
          :key="color.id"
          @click="onColorSelect(color)"
        >
          <v-list-tile-action>
            <span
              :class="['color-btn', `color-btn-${color.color}`]"
              :style="{backgroundColor: color.color}"
            >
              <v-icon
                size="18"
                v-if="isColorSelected(color)"
                :color="color.color === 'black' ? 'white' : 'black'"
              >fas fa-times</v-icon>
            </span>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ color.name }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list-group>
    </v-list>

    <addional-filters @change="onAdditionalFiltersChanged" />

    <!-- Filter tags -->
    <base-card color="accent" class="full-width-inside-base-container px-4 pb-3 pt-1">
      <FilterTags :filters="filters" />

      <tk-btn
        color="black"
        dark
        block
        large
        v-text="$t('SEARCH.FILTER_SUBMIT')"
        @click="$emit('search')"
      />
    </base-card>
  </div>
</template>

<script>
import { v4 as uuid } from "uuid";
import { RepositoryFactory } from "@/services/RepositoryFactory";
import { ErrorHandler } from "@/utils/ErrorHandler";
import { Size } from "@/services/size/Size";
import { Color } from "@/services/color/Color";
import { SearchFiltersTranslations } from "@/services/search/SearchFiltersTranslations";
import { SearchFilters } from "@/services/search/SearchFilters";

import TkSearchInput from "@/components/core/TkSearchInput.vue";
import PriceSlider from "../PriceSlider.vue";
import FilterTags from "./FilterTags.vue";
import AddionalFilters from "../../AddionalFilters.vue";

const translationsRepo = RepositoryFactory.translations();
const handler = new ErrorHandler();

export default {
  components: { TkSearchInput, PriceSlider, FilterTags, AddionalFilters },

  props: ["filters"],

  data: () => ({
    techniques: [], // list of translated techniques
    themes: [], // list of translated themes
    sizes: [], // list of translated sizes
    colors: [],
    filtersTranslations: null,
  }),

  created() {
    const opts = "categories,themes,size-ranges,colors"; // ,price-ranges";
    this.getTranslations(opts);
  },

  computed: {
    orientations() {
      return [
        { id: "portrait", name: this.$t("ARTWORK.PORTRAIT") },
        { id: "square", name: this.$t("ARTWORK.SQUARE") },
        { id: "landscape", name: this.$t("ARTWORK.LANDSCAPE") },
      ];
    },
  },

  methods: {
    async getTranslations(opts) {
      await handler.handle(async () => {
        const res = await translationsRepo.getTranslations(opts);

        this.themes = res.themes;
        this.sizes = Size.sizesFromArray(res.size_ranges);
        this.colors = Color.colorsFromObject(res.colors);

        this.setupTechniqueTree(res.categories);

        this.filtersTranslations = new SearchFiltersTranslations({
          sizes: this.sizes,
          colors: this.colors,
          orientations: this.orientations,
          themes: this.themes,
          techs: this.techniques,
        });

        this.queryMapFilters();
      });
    },

    setupTechniqueTree(categories) {
      this.techniques = categories.map((cat, i) => {
        return {
          id: uuid(),
          name: cat.name,
          slug: cat.slug,
          children: cat.techniques.map((technique, j) => {
            return {
              key: uuid(),
              id: technique.id,
              name: technique.name,
              slug: technique.slug,
            };
          }),
        };
      });
    },

    async queryMapFilters() {
      const query = this.$route.query;
      if (!query) return;

      const filtersFromQuery = new SearchFilters()
        .fromQuery(query)
        .translate(this.filtersTranslations);

      this.filters.techniquesSelected = filtersFromQuery.techniquesSelected;
      this.filters.orientationsSelected = filtersFromQuery.orientationsSelected;
      this.filters.themesSelected = filtersFromQuery.themesSelected;
      this.filters.pricesSelected = filtersFromQuery.pricesSelected;
      this.filters.sizesSelected = filtersFromQuery.sizesSelected;
      this.filters.colorSelected = filtersFromQuery.colorSelected;
      this.sortType = filtersFromQuery.sortType || "artworks";
    },

    onColorSelect(color) {
      this.filters.colorSelected = color;
    },

    onSelectedTechniquesChange(items) {
      this.filters.techniquesSelected = items;
    },

    isColorSelected(color) {
      return (
        this.filters.colorSelected &&
        this.filters.colorSelected.name === color.name
      );
    },

    onAdditionalFiltersChanged(e) {
      console.log("onAdditionalFiltersChanged", e);
      this.filters.framed = e.framed;
      this.filters.readyToHang = e.ready_to_hang;
      this.filters.forSale = e.for_sale;
      this.filters.video = e.video;
    },
  },
};
</script>

<style lang="scss">
.price-tile {
  .v-list__tile {
    padding: 0px;
  }
}
.color-btn {
  $size: 30px;
  min-width: $size;
  width: $size;
  height: $size;
  border-radius: 50%;
  display: flex;
  justify-content: center;

  &-white {
    border: 1px solid #000;
  }

  &-grayscale {
    border-right: calc($size / 2) solid black;
    border-left: calc($size / 2) solid white;
  }

  &:hover {
    opacity: 0.6;
  }
}

.color-tag {
  .color-dot {
    width: 16px;
    height: 16px;
    border: 1px solid #fff;
    border-radius: 50%;
    margin-right: 8px;

    &-grayscale {
      border-right: 8px solid #000;
      border-left: 8px solid #fff;
    }
  }
  .color-title {
    margin-bottom: 3px;
  }
}
</style>
